// Company-wide announcements — pick who gets it, type the message, send.
//
// One-way by design: the crew is told not to reply, and api/text/inbound.js refuses to
// act on a reply to an announcement (a bare "ok" would otherwise be read as a Y and
// confirm someone for a shift). Owner / road_super only.
import React, { useEffect, useMemo, useState } from 'react'
import { loadAnnounceAudience, sendAnnouncement } from '../lib/db'

function fmtPhone(p) {
  const d = String(p || '').replace(/\D/g, '')
  const ten = d.length === 11 && d[0] === '1' ? d.slice(1) : d
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : String(p || '')
}

function fmtWhen(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

// Mirrors segmentInfo() in api/lib/announce-audience.js so the count the office sees while
// typing matches what the server bills. Kept deliberately simple — the server is the
// authority and re-computes it on preview.
const GSM = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà"
const GSM_EXT = '^{}\\[~]|€'
function segmentInfo(text) {
  const s = String(text || '')
  const unicode = [...s].some(c => !GSM.includes(c) && !GSM_EXT.includes(c))
  const units = unicode ? [...s].length : [...s].reduce((n, c) => n + (GSM_EXT.includes(c) ? 2 : 1), 0)
  const single = unicode ? 70 : 160
  const multi = unicode ? 67 : 153
  return { units, unicode, segments: units === 0 ? 0 : units <= single ? 1 : Math.ceil(units / multi) }
}

export default function AnnouncementsPanel() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState(() => new Set())
  const [body, setBody] = useState('')
  const [confirm, setConfirm] = useState(null)   // the preview payload awaiting a yes
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)

  const refresh = async () => {
    setLoading(true); setErr('')
    const res = await loadAnnounceAudience()
    if (res.error) setErr(res.detail || res.error)
    else {
      setData(res)
      // Default selection = everyone who can actually be texted. This is the common case
      // (a company-wide notice); unchecking a few is easier than checking thirty.
      setPicked(new Set(res.audience.filter(p => p.eligible).map(p => p.key)))
    }
    setLoading(false)
  }
  useEffect(() => { refresh() }, [])

  const audience = data ? data.audience : []
  const q = query.trim().toLowerCase()
  // ONLY opted-in people are listed (Patrick, 2026-08-05). Someone who can't be texted is
  // not a choice to make, so showing them greyed out was just noise in the picker. The
  // count of who's missing still appears UNDER the list so their absence isn't silent —
  // that's a summary, not a row you could try to check.
  const shown = useMemo(
    () => audience.filter(p => p.eligible && (!q || String(p.name || '').toLowerCase().includes(q))),
    [audience, q])

  const selected = useMemo(() => audience.filter(p => p.eligible && picked.has(p.key)), [audience, picked])

  const toggle = (key) => setPicked(prev => {
    const next = new Set(prev)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })
  const setGroup = (group, on) => setPicked(prev => {
    const next = new Set(prev)
    audience.filter(p => p.eligible && (group === 'all' || p.group === group))
      .forEach(p => { if (on) next.add(p.key); else next.delete(p.key) })
    return next
  })

  // Must mirror withFooter() in api/lib/announce-audience.js EXACTLY — header + body +
  // footer — or the count shown while typing under-reports what the office is actually
  // billed for. Both wrappers come from the server (GET) rather than being re-typed here.
  const seg = segmentInfo(`${data ? data.header || '' : ''}${body.trim()}\n\n${data ? data.footer : ''}`)
  const maxChars = data ? data.max_body_chars : 1000
  const overLong = body.trim().length > maxChars
  const canSend = !!body.trim() && !overLong && selected.length > 0 && !busy

  const doPreview = async () => {
    setErr(''); setResult(null)
    setBusy(true)
    const res = await sendAnnouncement('preview', { body: body.trim(), keys: [...picked] })
    setBusy(false)
    if (res.error) { setErr(res.detail || res.error); return }
    setConfirm(res)
  }

  const doSend = async () => {
    setBusy(true)
    const res = await sendAnnouncement('send', { body: body.trim(), keys: [...picked] })
    setBusy(false)
    setConfirm(null)
    if (res.error) { setErr(res.detail || res.error); return }
    setResult(res)
    setBody('')
    refresh()
  }

  const c = data ? data.counts : null

  const groupRows = []
  let lastGroup = null
  for (const p of shown) {
    if (p.group !== lastGroup) {
      groupRows.push(
        <div key={'lbl-' + p.group} className="section-lbl">
          {p.group === 'crew' ? 'Crew' : 'Office & PMs'}
        </div>)
      lastGroup = p.group
    }
    groupRows.push(
      <label key={p.key}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px',
          borderBottom: '0.5px solid var(--border)', cursor: 'pointer',
        }}>
        <input type="checkbox" checked={picked.has(p.key)} onChange={() => toggle(p.key)}
          style={{ width: 18, height: 18, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            {p.name}
            {p.role === 'foreman' ? <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · foreman</span> : null}
            {p.group === 'office' && !p.active ? <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · not on roster</span> : null}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            {fmtPhone(p.phone)}{p.local ? ` · ${p.local}` : ''}
          </div>
        </div>
      </label>)
  }

  return (
    <div className="card">
      <div className="panel-header">
        <span className="panel-title">
          Company-wide text{' '}
          <span style={{ color: 'var(--text3)', fontWeight: 400, fontSize: 12 }}>
            {c ? `${selected.length} selected of ${c.eligible} reachable` : ''}
          </span>
        </span>
        <button className="btn btn-sm" onClick={refresh} disabled={loading}>Refresh</button>
      </div>

      {err && <div style={{ color: '#c9302c', fontSize: 12, padding: '8px 12px' }}>{err}</div>}
      {loading && !data && (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>Loading…</div>
      )}

      {result && (
        <div style={{ margin: '10px 12px', padding: '10px 12px', borderRadius: 8, background: 'rgba(52,199,89,0.13)', fontSize: 13 }}>
          <strong>Sent to {result.sent} {result.sent === 1 ? 'person' : 'people'}.</strong>
          {result.failed > 0 && <div style={{ color: '#c9302c', marginTop: 4 }}>
            {result.failed} failed: {(result.failures || []).map(f => f.name).join(', ')}
          </div>}
          {result.not_sent > 0 && <div style={{ color: '#c9302c', marginTop: 4 }}>
            {result.not_sent} not sent — the send ran out of time. Send to them separately.
          </div>}
          {(result.skipped || []).length > 0 && <div style={{ color: 'var(--text3)', marginTop: 4 }}>
            Skipped {result.skipped.length}: {result.skipped.map(s => s.name).join(', ')}
          </div>}
        </div>
      )}

      {data && (
        <div style={{ display: 'flex', gap: 14, padding: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* ── Recipients ─────────────────────────────────────────── */}
          <div style={{ flex: '1 1 340px', minWidth: 300 }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingBottom: 8 }}>
              <button className="btn btn-sm" onClick={() => setGroup('all', true)}>Select all ({c.eligible})</button>
              <button className="btn btn-sm" onClick={() => setGroup('all', false)}>None</button>
              <button className="btn btn-sm" onClick={() => { setGroup('all', false); setGroup('crew', true) }}>Crew only ({c.crew_eligible})</button>
              <button className="btn btn-sm" onClick={() => { setGroup('all', false); setGroup('office', true) }}>Office only ({c.office_eligible})</button>
            </div>
            <input className="form-input" type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search by name…" style={{ width: '100%', marginBottom: 8 }} />
            <div style={{ maxHeight: 420, overflowY: 'auto', border: '0.5px solid var(--border)', borderRadius: 8 }}>
              {groupRows.length === 0
                ? <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                    {q ? 'Nobody matches that search.' : 'Nobody has opted in to texts yet.'}
                  </div>
                : groupRows}
            </div>
            {(c.no_optin > 0 || c.no_phone > 0) && (
              <div style={{ fontSize: 11, color: 'var(--text3)', paddingTop: 6 }}>
                <strong>Not shown:</strong>{' '}
                {c.no_optin > 0 && <>{c.no_optin} {c.no_optin === 1 ? 'person hasn' : 'people haven'}&rsquo;t opted in — they can sign up at <strong>cornerstonecrewdispatch.com/sms-optin.html</strong>{c.no_phone > 0 ? '; ' : '.'}</>}
                {c.no_phone > 0 && <>{c.no_phone} {c.no_phone === 1 ? 'has' : 'have'} no phone on file.</>}
              </div>
            )}
          </div>

          {/* ── Message ────────────────────────────────────────────── */}
          <div style={{ flex: '1 1 340px', minWidth: 300 }}>
            <textarea className="form-input" rows={7} value={body} onChange={e => setBody(e.target.value)}
              placeholder={'Type the message…\n\ne.g. Reminder: the office is closed Monday for Labor Day. No crews scheduled.'}
              style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: overLong ? '#c9302c' : 'var(--text3)', paddingTop: 4 }}>
              <span>{body.trim().length} / {maxChars} characters</span>
              <span>
                {seg.segments} segment{seg.segments === 1 ? '' : 's'} × {selected.length} = {seg.segments * selected.length} messages
                {seg.unicode ? ' · special characters (fewer per segment)' : ''}
              </span>
            </div>

            <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'var(--surface)', border: '0.5px solid var(--border)', fontSize: 12, color: 'var(--text3)' }}>
              <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 3 }}>Automatically added:</div>
              <div>{data.header ? <><strong>{data.header.trim()}</strong> at the start &mdash; announcements come from a different number, so every message names the sender.</> : null}</div>
              <div style={{ marginTop: data.header ? 3 : 0 }}>{data.footer}</div>
            </div>

            {data.outside_quiet_hours && (
              <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,159,10,0.13)', fontSize: 12 }}>
                ⚠ It&rsquo;s outside 7am&ndash;8pm ET. Crew reminders are normally held until 7am — send now only if it&rsquo;s urgent.
              </div>
            )}

            <button className="btn btn-primary" disabled={!canSend} onClick={doPreview}
              style={{ marginTop: 10, width: '100%' }}>
              {busy ? 'Working…' : `Review before sending (${selected.length} ${selected.length === 1 ? 'person' : 'people'})`}
            </button>

            {(data.history || []).length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div className="section-lbl" style={{ padding: '0 0 4px' }}>Recent announcements</div>
                {data.history.map(h => (
                  <div key={h.id} style={{ padding: '6px 0', borderBottom: '0.5px solid var(--border)' }}>
                    <div style={{ fontSize: 12 }}>{h.body.length > 90 ? h.body.slice(0, 90) + '…' : h.body}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {fmtWhen(h.sent_at)} · {h.sent_by_name || h.sent_by} · {h.sent_count} sent
                      {h.failed_count ? ` · ${h.failed_count} failed` : ''}
                      {h.skipped_count ? ` · ${h.skipped_count} skipped` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Confirm ──────────────────────────────────────────────── */}
      {confirm && (() => {
        const crew = confirm.recipients.filter(r => r.group === 'crew')
        const office = confirm.recipients.filter(r => r.group === 'office')
        // .section-lbl paints an edge-to-edge grey bar — right for the list, wrong inside a
        // padded modal. Plain label here instead.
        const lbl = (text, top = 12) => (
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, padding: `${top}px 0 4px` }}>{text}</div>
        )
        const nameList = (list) => (
          // Deliberately WRAPS rather than truncating: this screen exists to be verified, so
          // a long name must never hide the phone behind an ellipsis.
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))', gap: '3px 12px', padding: '4px 0 8px' }}>
            {list.map(r => (
              <div key={r.phone} style={{ fontSize: 12, lineHeight: 1.35 }}>
                {r.name} <span style={{ color: 'var(--text3)', whiteSpace: 'nowrap' }}>{fmtPhone(r.phone)}</span>
              </div>
            ))}
          </div>
        )
        return (
          <div className="modal-backdrop" onClick={() => !busy && setConfirm(null)}>
            {/* overflowY hidden: .modal sets auto, which would double-scroll against the
                inner scroller below. maxHeight accounts for the backdrop's 60px top pad. */}
            <div className="modal" onClick={e => e.stopPropagation()}
              style={{ maxWidth: 640, width: '94%', maxHeight: 'calc(100vh - 100px)', overflowY: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-title">Review before sending</div>
              <div className="modal-sub">
                Going to <strong>{confirm.recipient_count} {confirm.recipient_count === 1 ? 'person' : 'people'}</strong>
                {' '}· {confirm.segments} segment{confirm.segments === 1 ? '' : 's'} each · {confirm.total_messages} messages total.
                Once sent it cannot be unsent.
              </div>

              <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                {lbl('The message they’ll get', 4)}
                <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.45, padding: '10px 12px', borderRadius: 8, background: 'var(--surface)', border: '0.5px solid var(--border)' }}>
                  {confirm.full_body}
                </div>

                {confirm.outside_quiet_hours && (
                  <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,159,10,0.13)', fontSize: 12 }}>
                    ⚠ It&rsquo;s outside 7am&ndash;8pm ET — send now only if it&rsquo;s urgent.
                  </div>
                )}

                {/* Anyone dropped BETWEEN loading the page and pressing send — i.e. their
                    consent changed under you. Rare, but it must be visible, not silent. */}
                {confirm.skipped.length > 0 && (
                  <div style={{ marginTop: 8, padding: '8px 10px', borderRadius: 8, background: 'rgba(255,159,10,0.13)', fontSize: 12 }}>
                    ⚠ {confirm.skipped.length} selected {confirm.skipped.length === 1 ? 'person' : 'people'} can no longer be texted and will be skipped:{' '}
                    {confirm.skipped.map(s => `${s.name} (${s.reason === 'no_phone' ? 'no phone' : 'not opted in'})`).join(', ')}
                  </div>
                )}

                {crew.length > 0 && (
                  <>
                    {lbl(`Crew (${crew.length})`)}
                    {nameList(crew)}
                  </>
                )}
                {office.length > 0 && (
                  <>
                    {lbl(`Office & PMs (${office.length})`, 8)}
                    {nameList(office)}
                  </>
                )}
              </div>

              <div className="modal-actions">
                <button className="btn" onClick={() => setConfirm(null)} disabled={busy}>Back</button>
                <button className="btn btn-primary" onClick={doSend} disabled={busy}>
                  {busy ? 'Sending…' : `Send to ${confirm.recipient_count}`}
                </button>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
